import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import default_image from "../assets/dafault_three_images.png";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { X } from "lucide-react";

// ImageViewer Props
type ImageViewerProps = {
  images: string[];
  initialIndex: number;
  onClose: () => void;
};

// Modal Carousel Viewer
export const ImageViewer = ({ images, initialIndex, onClose }: ImageViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const swiperRef = useRef<any>(null);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = default_image;
  };

  const goToImage = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 cursor-pointer"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 text-white text-3xl z-50"
        aria-label="Close Image Popup"
      >
        <X />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full px-5 max-w-4xl h-[80vh] flex justify-center items-center"
      >
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          initialSlide={initialIndex}
          onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
          spaceBetween={10}
          className="w-full h-full"
        >
          {images.map((img, idx) => (
            <SwiperSlide key={idx}>
              <img
                src={img}
                alt={`Slide ${idx}`}
                onError={handleImageError}
                className="object-contain h-full mx-auto"
              />
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute bottom-[10%] flex space-x-2 justify-center w-full">
          {images.map((_, index) => (
            <div
              key={index}
              className={`h-1 w-6 rounded-md cursor-pointer transition-all duration-300 ${
                index === currentIndex
                  ? "bg-white scale-110"
                  : "bg-gray-400 opacity-50"
              }`}
              onClick={() => goToImage(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// ThreeImageGrid Props
export type ThreeImageGridProps = {
  className?: string;
  images?: string[];
};

// Grid Component
const ThreeImageGrid = ({ className, images }: ThreeImageGridProps) => {
  const location = useLocation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  let height = "h-[201px]";
  if (location.pathname === "/") {
    height = "h-[170px]";
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = default_image;
  };

  const openImagePopup = (image: string, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  return (
    <>
      <div className={`flex w-full ${height} overflow-hidden rounded-xl ${className ?? ""} lg:aspect-square`}>
        {images && images.length > 0 ? (
          images.map((image, i) => (
            <img
              key={i}
              src={image}
              alt={`image-${i}`}
              onClick={() => openImagePopup(image, i)}
              onError={handleImageError}
              className="w-1 flex-1 h-full object-cover cursor-pointer"
            />
          ))
        ) : (
          <img
            src={default_image}
            alt="default image"
            className="w-1 flex-1 h-full object-cover"
          />
        )}
      </div>

      {/* Popup Modal Carousel */}
      {selectedImage && (
        <ImageViewer
          images={images ?? []}
          initialIndex={currentIndex}
          onClose={closeImagePopup}
        />
      )}
    </>
  );
};

export default ThreeImageGrid;
